import { defineStore } from "pinia";
import axios from "../plugins/axios";
import {handleApiException} from "@/composables/exceptions.js";
import {useRouter} from "vue-router";

const BASE_API_URL = import.meta.env.VITE_BASE_API_URL;
const CSRF_URL = baseUrl() + "sanctum/csrf-cookie";
const USER_URL = BASE_API_URL + "auth/user";
const LOGIN_URL = BASE_API_URL + "auth/login";
const LOGOUT_URL = BASE_API_URL + "auth/logout";
const REGISTER_PREPARE_URL = BASE_API_URL + "auth/register/prepare";
const REGISTER_GENERATE_AGREEMENT_URL = BASE_API_URL + "auth/register/generate-agreement";
const REGISTER_COMPLETE_URL = BASE_API_URL + "auth/register/complete";
const UPDATE_PASSWORD_URL = BASE_API_URL + "auth/password";
const CONFIRM_EMAIL_URL = BASE_API_URL + "auth/confirm-email";
const SEND_CONFIRMATION_EMAIL_URL = BASE_API_URL + "auth/send-confirmation-email";
const SEND_UNVERIFIED_CONFIRMATION_EMAIL_URL = BASE_API_URL + "auth/send-unverified-confirmation-email";
const CONFIRM_UNVERIFIED_EMAIL_URL = BASE_API_URL + "auth/confirm-unverified-email";
const ADD_WALLET_URL = BASE_API_URL + "auth/user/add-wallet";

function baseUrl() {
  const searchString = 'api/';
  const url = import.meta.env.VITE_BASE_API_URL;
  if (url.endsWith(searchString)) {
    return url.substring(0, url.length - searchString.length);
  }
  return url;
}


export const useAuth = defineStore("auth", {
  state: () => ({
    user: null,
    showModal: false,
    showRegisterForm: false,
    postingWalletAddress: false,
  }),
  getters: {
    getUser: (state) => state.user,
  },
  actions: {
    setAuthToken(token) {
      try {
        if (!token) {
          localStorage.removeItem('auth_token');
          delete axios.defaults.headers.common['Authorization'];
          return;
        }
        localStorage.setItem('auth_token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (_) {
        // ignore storage errors
      }
    },
    showAuthModal() {
      this.showModal = true;
    },
    showRegisterModal() {
      this.showModal = true;
      this.showRegisterForm = true;
    },
    closeModal() {
      this.showModal = false;
      this.showRegisterForm = false;
    },
    async loadUser() {
      //await axios.get(CSRF_URL);
      try {
        const response = await axios.get(USER_URL);
        this.user = response.data.data.user;
        if (response.data.data.contact) {
          this.user = { ...this.user, contact: response.data.data.contact };
        }
        return response.data.data;
      }
      catch (e) {
        handleApiException(e);
      }
    },
    async prepareRegistration(user) {
      await axios.get(CSRF_URL);
      const response = await axios.post(REGISTER_PREPARE_URL, user);
      return response.data.data;
    },
    async generateRegistrationAgreement(user) {
      await axios.get(CSRF_URL);
      const response = await axios.post(REGISTER_GENERATE_AGREEMENT_URL, user);
      return response.data.data;
    },
    async completeRegistration(userId, documentUuid) {
      await axios.get(CSRF_URL);
      const response = await axios.post(REGISTER_COMPLETE_URL, {
        user_id: userId,
        document_uuid: documentUuid
      });
      // Do not auto-login user - they must verify email first
      // this.setUser(response.data.data.user);
      return response.data.data;
    },
    async login(username, password) {
      await axios.get(CSRF_URL);
      const loginResponse = await axios.post(LOGIN_URL, {
        email: username,
        password: password,
      });
      if (loginResponse.data && loginResponse.data.data && loginResponse.data.data.token) {
        this.setAuthToken(loginResponse.data.data.token);
      }
      const response = await axios.get(USER_URL);
      this.setUser(response.data.data.user);
    },
    setUser(userData) {
      this.user = userData;
    },
    async logout() {
      try {
        await axios.post(LOGOUT_URL);
      } catch (_) {
        // ignore network/API errors on logout
      } finally {
        this.setAuthToken(null);
        this.user = null;
        window.open('/', '_self');
      }
    },
    async updatePassword(attributes) {
      return axios.post(UPDATE_PASSWORD_URL, attributes);
    },
    async sendConfirmationEmail() {
      return axios.post(SEND_CONFIRMATION_EMAIL_URL);
    },
    async sendUnverifiedConfirmationEmail(email) {
      return axios.post(SEND_UNVERIFIED_CONFIRMATION_EMAIL_URL, { email });
    },
    async confirmEmail(code) {
      return axios.post(CONFIRM_EMAIL_URL, {
        code: code
      });
    },
    async confirmUnverifiedEmail(email, code) {
      return axios.post(CONFIRM_UNVERIFIED_EMAIL_URL, {
        email: email,
        code: code
      });
    },
    async addWalletAddress(walletAccountAddress) {
      if (this.postingWalletAddress) {
        return;
      }
      try {
        this.postingWalletAddress = true;
        await axios.post(ADD_WALLET_URL,{account_number: walletAccountAddress});
      }
      catch (e) {
        handleApiException(e);
      }
      finally {
        this.postingWalletAddress = false;
      }
    },
    async addWalletAddresses(walletAccountAddresses) {
      if (this.postingWalletAddress) {
        return;
      }
      try {
        this.postingWalletAddress = true;
        await axios.post(ADD_WALLET_URL,{account_number: walletAccountAddresses});
      }
      catch (e) {
        handleApiException(e);
      }
      finally {
        this.postingWalletAddress = false;
      }
    },
  },
});
